import { queryClient } from "@src/utils/queryClient";
import StorageUtils from "@src/utils/storageUtils";
import AppUtils from "@src/utils/appUtils";
import SignolUtils from "@src/utils/signolUtils";
import {
  ACCESS_TOKEN_COOKIE,
  LOGIN_URL,
  REFRESH_TOKEN_COOKIE,
  SAML_SSO_COOKIE,
  USER_COOKIE,
  STALE_TIME_1HS,
  SSO_LOGIN_ATTEMPT_COOKIE,
} from "@src/constants/constants";
import { authEndpoints } from "@src/services/endpoints";
import {
  trackActionActivity,
  trackLoginError,
  trackSession,
} from "../utils/userActivityTracker";

/**
 * This loader ensures the user is authenticated before rendering any protected routes.
 * It handles the initial session validation and fetches the user's identity.
 */
const authLoader = async ({ request }) => {
  try {
    // Handle SAML login redirection if coming from an external Identity Provider.
    const queryparams = new URL(request.url).searchParams;
    const samlIdPRedirection = queryparams.get("saml") === "true";
    if (samlIdPRedirection) {
      const authCode = queryparams.get("code");
      if (AppUtils.isValidUUID(authCode)) {
        trackActionActivity("SAML_REDIRECT", { code: authCode });
        const { accessToken, refreshToken } =
          await authEndpoints.exchangeSSO(authCode);
        StorageUtils.setItem(ACCESS_TOKEN_COOKIE, accessToken);
        StorageUtils.setItem(REFRESH_TOKEN_COOKIE, refreshToken);
        StorageUtils.setItem(SAML_SSO_COOKIE, true);
        StorageUtils.setItem(LOGIN_URL, "/loginEmail");
      } else {
        const error = queryparams.get("error") || "SAML authentication failed";
        const errorDescription = decodeURIComponent(
          queryparams.get("error_description").replace(/\+/g, " "),
        );
        console.error(`[${String(error).toUpperCase()}] ${errorDescription}`);
        trackLoginError(error, "SAML_AUTH_ERROR", {
          ...Object.fromEntries(queryparams.entries()),
          error,
          errorDescription,
          signolInputEmail: StorageUtils.getItem(SSO_LOGIN_ATTEMPT_COOKIE),
        });
        return null;
      }
    }

    const refreshToken = StorageUtils.getItem(REFRESH_TOKEN_COOKIE);
    //Sanity check because Demo sets weird storage values
    const refreshTokenLooksValid = AppUtils.isMockedApp
      ? String(refreshToken).match(/\d+/)
      : typeof refreshToken === "string" && refreshToken.split(".").length >= 3;
    if (!refreshTokenLooksValid) {
      return null;
    }

    const user = queryClient
      .ensureQueryData({
        queryKey: ["IDENTITY_ME"],
        staleTime: STALE_TIME_1HS,
        queryFn: async () => {
          const currentUser = StorageUtils.getObjectItem(USER_COOKIE);
          return authEndpoints.getMeIdentity(currentUser?.username);
        },
      })
      .then((identityProfileData) => {
        trackSession(identityProfileData);
        return identityProfileData;
      });
    return user;
  } catch (error) {
    console.error(error);
    queryClient.invalidateQueries();
    trackLoginError(error, "LOGIN_ERROR", {
      signolInputEmail: StorageUtils.getItem(SSO_LOGIN_ATTEMPT_COOKIE),
    });
    SignolUtils.removeSessionCookies();
    return null;
  }
};

export default authLoader;
